import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Sparkles, X, ZoomIn, CheckCircle2, TrendingUp, Lock, ShieldAlert } from 'lucide-react';

export default function About() {
  const [selectedImage, setSelectedImage] = useState(null);

  // Certification Data
  const certificates = [
    {
      id: 1,
      title: "NISM Series V-A",
      issuer: "National Institute of Securities Markets",
      description: "Mutual Fund Distributors Certification Examination",
      date: "Certified Expert",
      bgImage: "https://images.unsplash.com/photo-1589330694653-aded6fac051d?auto=format&fit=crop&q=80&w=800"
    },
    {
      id: 2,
      title: "NISM Series VIII",
      issuer: "National Institute of Securities Markets",
      description: "Equity Derivatives Certification Examination",
      date: "Certified Expert",
      bgImage: "https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?auto=format&fit=crop&q=80&w=800"
    },
    {
      id: 3,
      title: "IRDAI Corporate Agent",
      issuer: "Insurance Regulatory and Development Authority",
      description: "Insurance Advisory & Risk Management Registration",
      date: "Certified Expert",
      bgImage: "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&q=80&w=800"
    },
    {
      id: 4,
      title: "Financial Planning & Wealth Excellence",
      issuer: "Welvate Advisory Board",
      description: "Recognized for Ethical Wealth Guidance & Student Mentorship",
      date: "Excellence Award",
      bgImage: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?auto=format&fit=crop&q=80&w=800"
    }
  ];

  const financialSolutions = [
    "Stock Market Investments",
    "Mutual Funds",
    "Bonds",
    "Insurance Solutions – Life, Health & General",
    "Portfolio Management Services (PMS)",
    "Alternative Investment Funds (AIF)",
    "Specialized Investment Funds (SIF)",
    "Financial Planning",
    "Wealth Management",
    "Retirement Planning",
    "Goal-Based Investment Planning",
    "Tax-Efficient Investment Solutions"
  ];

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans selection:bg-[#16325B] selection:text-white pb-20">
      
      {/* SECTION 1: MEET HARITIKA */}
      <section className="pt-12 pb-16 px-6 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] border border-[#C7D7E8] text-[#16325B] text-xs font-semibold px-4 py-1.5 rounded-full">
            <Sparkles className="w-4 h-4 text-[#16325B]" />
            Meet The Founder
          </div>
          <h1 className="font-serif-title text-4xl sm:text-6xl font-extrabold text-[#0B2540] tracking-tight">
            Meet <span className="text-[#16325B] italic font-normal">Haritika</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Founder Image Box */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden bg-white border border-gray-200/80 p-3 shadow-xl">
              <div className="h-[460px] rounded-2xl overflow-hidden relative bg-slate-100">
                <img 
                  src="/harritika.jpg (1).jpeg" 
                  alt="Haritika - Trader, Investor & Financial Consultant" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2540]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white space-y-1 z-10">
                  <h3 className="font-serif-title text-2xl font-bold">Haritika</h3>
                  <p className="text-xs text-[#16325B] font-semibold tracking-wide uppercase bg-white/95 px-3 py-1 rounded-full inline-block">
                    Trader, Investor & Financial Consultant
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Founder Detailed Content */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-block bg-[#EEF4FA] text-[#16325B] font-bold text-xs px-3.5 py-1 rounded-lg">
              8+ Years of Financial Markets Experience
            </div>

            <p className="text-gray-700 text-base sm:text-lg leading-relaxed font-medium">
              Haritika is a trader, investor, and financial consultant with over 8 years of experience in the financial markets.
            </p>

            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Her journey in the stock market began in <strong>2018</strong>, and since <strong>2021</strong>, she has been professionally guiding individuals and families in making informed financial decisions.
            </p>

            <div className="p-5 bg-white border-l-4 border-[#16325B] rounded-r-2xl shadow-sm italic text-gray-800 text-sm sm:text-base">
              "Financial decisions should be driven by knowledge, planning, and clearly defined goals — not emotions or market noise."
            </div>

            <p className="text-gray-600 text-sm leading-relaxed">
              Through Welvate, Haritika aims to make financial planning simpler, more transparent, and more accessible. Whether you are taking your first steps into investing or looking to strengthen and optimize your existing portfolio, her approach focuses on understanding your financial goals, risk profile, and long-term aspirations before recommending a strategy.
            </p>

            {/* 3 Priorities Banner */}
            <div className="pt-2">
              <h4 className="font-bold text-xs uppercase text-gray-500 tracking-wider mb-3">
                Complete Financial Roadmap Built Around 3 Priorities:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 text-center font-bold text-xs text-[#0B2540] shadow-sm">
                  Wealth Creation
                </div>
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 text-center font-bold text-xs text-[#0B2540] shadow-sm">
                  Financial Security
                </div>
                <div className="bg-white p-3.5 rounded-xl border border-gray-200 text-center font-bold text-xs text-[#0B2540] shadow-sm">
                  Long-Term Independence
                </div>
              </div>
            </div>

            <div className="p-5 bg-[#0B2540] text-white rounded-2xl space-y-1">
              <h4 className="font-serif-title font-bold text-[#60A5FA] text-sm">Her Mission</h4>
              <p className="text-xs text-gray-300 leading-relaxed">
                To empower people with the right knowledge, strategy, and financial guidance to take control of their financial future.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 2: CERTIFICATIONS & ACHIEVEMENTS */}
      <section className="py-16 px-6 max-w-7xl mx-auto border-t border-gray-200/80">
        <div className="text-center space-y-3 mb-10">
          <div className="inline-flex items-center gap-1.5 text-[#16325B] font-bold text-xs uppercase tracking-widest">
            <CheckCircle2 className="w-4 h-4 text-[#16325B]" /> Verified Credentials
          </div>
          <h2 className="font-serif-title text-3xl sm:text-4xl font-bold text-[#0B2540]">
            Haritika's Certifications & Achievements
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm max-w-xl mx-auto">
            Rigorous regulatory qualifications ensuring compliant, expert-driven financial education and wealth advisory.
          </p>
        </div>

        {/* Certificate Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              onClick={() => setSelectedImage(cert)}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-200/80 shadow-sm hover:shadow-xl hover:border-[#16325B]/40 transition-all duration-300 cursor-pointer flex flex-col justify-between"
            >
              <div>
                <div className="h-44 w-full bg-slate-100 overflow-hidden relative">
                  <img
                    src={cert.bgImage}
                    alt={cert.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-[#0B2540]/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="bg-white/20 backdrop-blur-md p-2.5 rounded-full border border-white/40">
                      <ZoomIn className="w-5 h-5 text-white" />
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#16325B] bg-[#EEF4FA] px-2.5 py-0.5 rounded-md inline-block">
                    {cert.date}
                  </span>
                  <h3 className="font-serif-title text-lg font-bold text-[#0B2540] group-hover:text-[#16325B] transition-colors leading-snug">
                    {cert.title}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium">
                    {cert.issuer}
                  </p>
                  <p className="text-[11px] text-gray-400 line-clamp-2 pt-1">
                    {cert.description}
                  </p>
                </div>
              </div>

              <div className="px-5 pb-5 pt-2 flex items-center justify-end">
                <span className="text-xs font-bold text-[#16325B] flex items-center gap-1 group-hover:underline">
                  View Certificate →
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 3: ABOUT WELVATE */}
      <section className="py-16 px-6 max-w-7xl mx-auto border-t border-gray-200/80">
        
        {/* Welvate Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] border border-[#C7D7E8] text-[#16325B] text-xs font-semibold px-4 py-1.5 rounded-full">
            Elevating Wealth. Empowering Futures.
          </div>
          <h2 className="font-serif-title text-3xl sm:text-5xl font-bold text-[#0B2540]">
            About <span className="text-[#16325B]">Welvate</span>
          </h2>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Welvate is a modern financial services and wealth management company dedicated to helping individuals, families, and businesses make informed financial decisions and build, protect, and preserve long-term wealth.
          </p>
        </div>

        {/* Philosophy Intro */}
        <div className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-sm max-w-4xl mx-auto space-y-4 mb-16">
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            We believe that <strong>no two financial journeys are the same</strong>. That’s why our approach is built around understanding each client’s financial goals, risk profile, priorities, and future aspirations before creating a suitable financial strategy.
          </p>
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            At Welvate, we combine <strong>personalized guidance, research-driven insights, and a long-term approach</strong> to make financial planning simpler, more transparent, and more meaningful for our clients.
          </p>
        </div>

        {/* Financial Solutions Grid */}
        <div className="space-y-6 mb-16">
          <div className="text-center">
            <h3 className="font-serif-title text-2xl sm:text-3xl font-bold text-[#0B2540]">
              Our Financial Solutions
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Comprehensive solutions designed for every stage of your financial journey.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {financialSolutions.map((solution, idx) => (
              <div 
                key={idx}
                className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-sm flex items-center gap-3 hover:border-[#16325B]/40 transition"
              >
                <div className="w-8 h-8 rounded-full bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0 font-bold text-xs">
                  ✓
                </div>
                <span className="text-xs sm:text-sm font-bold text-[#0B2540]">{solution}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Our Approach (4 Pillars) */}
        <div className="space-y-8 mb-16">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-serif-title text-2xl sm:text-3xl font-bold text-[#0B2540]">
              Our Approach
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">
              Successful wealth creation is not about chasing market trends or emotional decisions. It is about having the <strong>right plan, strategy, and discipline to stay invested.</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-200/80 text-center space-y-2 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center mx-auto">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h4 className="font-serif-title font-bold text-[#0B2540] text-base">Grow Wealth</h4>
              <p className="text-xs text-gray-500">Disciplined investing in compounding assets.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/80 text-center space-y-2 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center mx-auto">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <h4 className="font-serif-title font-bold text-[#0B2540] text-base">Protect Wealth</h4>
              <p className="text-xs text-gray-500">Risk mitigation & insurance strategies.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/80 text-center space-y-2 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center mx-auto">
                <Lock className="w-5 h-5" />
              </div>
              <h4 className="font-serif-title font-bold text-[#0B2540] text-base">Preserve Wealth</h4>
              <p className="text-xs text-gray-500">Tax-efficient allocation & estate care.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200/80 text-center space-y-2 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center mx-auto">
                <Target className="w-5 h-5" />
              </div>
              <h4 className="font-serif-title font-bold text-[#0B2540] text-base">Build Independence</h4>
              <p className="text-xs text-gray-500">Long-term freedom & goal fulfillment.</p>
            </div>
          </div>
        </div>

        {/* Welvate Mission Footer Banner */}
        <div className="bg-[#0B2540] text-white rounded-3xl p-8 sm:p-12 text-center space-y-4 shadow-xl">
          <span className="text-[#60A5FA] font-bold text-xs uppercase tracking-widest">Company Mission</span>
          <h3 className="font-serif-title text-2xl sm:text-4xl font-bold max-w-3xl mx-auto leading-snug">
            "To simplify finance, empower investors through knowledge, and provide ethical, transparent, and research-driven solutions."
          </h3>
          <p className="text-gray-300 text-xs sm:text-sm max-w-2xl mx-auto">
            At Welvate, we don't just look at where your money is today. <strong>We look at where you want it to take you tomorrow.</strong>
          </p>
        </div>

      </section>

      {/* LIGHTBOX MODAL FOR CERTIFICATES */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl relative p-2"
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-black text-white p-2 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="max-h-[75vh] overflow-hidden rounded-xl bg-slate-100">
                <img
                  src={selectedImage.bgImage}
                  alt={selectedImage.title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-5 space-y-1 bg-white">
                <h3 className="font-serif-title text-xl font-bold text-[#0B2540]">
                  {selectedImage.title}
                </h3>
                <p className="text-xs font-semibold text-[#16325B]">
                  {selectedImage.issuer}
                </p>
                <p className="text-xs text-gray-500 pt-1">
                  {selectedImage.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}